import React, { useEffect, useState } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { apiFetch } from "../api/apiFetch";
import { formatDate, formatNumber } from "../utils/format";
import { formatStockInfo } from "../utils/stock";
import ProductTimeline from "../components/ProductTimeline";
import ErrorModal from "../components/ErrorModal";
import { exportSingleProductLabelToPDF } from "../utils/productLabelsPdf";

export default function ProductDetail({ user }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [prices, setPrices] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [stockInfo, setStockInfo] = useState(null);
  const [unauthorized, setUnauthorized] = useState(false);
  const [showPriceModal, setShowPriceModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorModal, setErrorModal] = useState({ show: false, message: "" });
  const [exportingLabel, setExportingLabel] = useState(false);
  const [timelineFilters, setTimelineFilters] = useState({
    purchase: true,
    sale: true,
    price_update: true,
    stock_count: true,
  });
  const [priceFormData, setPriceFormData] = useState({
    recorded_at: new Date().toISOString().split("T")[0],
    supplier_id: "",
    price_excl_discount: "",
    price_incl_discount: "",
    notes: "",
  });

  useEffect(() => {
    if (!user) return;
    fetchProductData();
    fetchSuppliers();
  }, [user, id]);

  const fetchProductData = () => {
    // Fetch product details
    apiFetch(`/products/${id}`)
      .then(setProduct)
      .catch((err) => {
        console.error("Error fetching product:", err);
        if (err.message.includes("403")) setUnauthorized(true);
      });

    // Fetch price history
    apiFetch(`/products/${id}/prices`)
      .then(setPrices)
      .catch((err) => {
        console.error("Error fetching prices:", err);
        if (err.message.includes("403")) setUnauthorized(true);
      });

    // Fetch stock info (confirmed + estimated, summed across all locations)
    apiFetch(`/products/${id}/stock-info`)
      .then(setStockInfo)
      .catch((err) => {
        console.error("Error fetching stock info:", err);
      });

    // Fetch locations
    apiFetch(`/products/${id}/locations`)
      .then(setLocations)
      .catch((err) => {
        console.error("Error fetching locations:", err);
      });

    // Fetch timeline
    apiFetch(`/products/${id}/timeline`)
      .then((data) => {
        console.log("Timeline response:", data);
        setTimeline(data.timeline || []);
      })
      .catch((err) => {
        console.error("Error fetching timeline:", err);
        if (err.message.includes("403")) setUnauthorized(true);
      });
  };

  const fetchSuppliers = () => {
    apiFetch('/suppliers?sort=name&order=asc')
      .then(setSuppliers)
      .catch((err) => {
        console.error("Error fetching suppliers:", err);
        if (err.message.includes("403")) setUnauthorized(true);
      });
  };

  const handleAddPrice = () => {
    setPriceFormData({
      recorded_at: new Date().toISOString().split("T")[0],
      supplier_id: "",
      price_excl_discount: "",
      price_incl_discount: "",
      notes: "",
    });
    setShowPriceModal(true);
  };

  const handleSubmitPrice = (e) => {
    e.preventDefault();
    setLoading(true);

    const payload = {
      recorded_at: priceFormData.recorded_at || null,
      supplier_id: priceFormData.supplier_id ? parseInt(priceFormData.supplier_id) : null,
      price_excl_discount: parseFloat(priceFormData.price_excl_discount),
      price_incl_discount: parseFloat(priceFormData.price_incl_discount),
      notes: priceFormData.notes || null,
    };

    apiFetch(`/products/${id}/prices`, {
      method: "POST",
      body: JSON.stringify(payload),
    })
      .then(() => {
        setShowPriceModal(false);
        setPriceFormData({
          recorded_at: new Date().toISOString().split("T")[0],
          supplier_id: "",
          price_excl_discount: "",
          price_incl_discount: "",
          notes: "",
        });
        fetchProductData(); // Refresh data
      })
      .catch((err) => {
        console.error("Error adding price:", err);
        setErrorModal({ show: true, message: "Er is een fout opgetreden bij het toevoegen van de prijs." });
        if (err.message.includes("403")) setUnauthorized(true);
      })
      .finally(() => setLoading(false));
  };

  if (unauthorized) return <Navigate to="/unauthorized" replace />;
  if (!product) return <div className="p-6">Loading...</div>;

  // For price tile: latest non-manual price for date & supplier
  const latestInvoicePrice = prices.find(p => !p.is_manual) || null;

  // Use backend stock-info (correctly sums across all locations)
  const formattedStockInfo = formatStockInfo(stockInfo);

  // Filter timeline based on selected filters
  // Note: selling_price_update events are shown when price_update filter is active
  const filteredTimeline = timeline.filter(event => {
    if (event.event_type === 'selling_price_update') {
      return timelineFilters.price_update;
    }
    return timelineFilters[event.event_type];
  });

  // Toggle filter function
  const toggleFilter = (filterType) => {
    setTimelineFilters(prev => ({
      ...prev,
      [filterType]: !prev[filterType]
    }));
  };

  // Check if product has notes
  const hasNotes = product.description && product.description.trim() !== '';

  // Handle exporting single product label
  const handleExportLabel = async () => {
    setExportingLabel(true);
    try {
      await exportSingleProductLabelToPDF({
        id: product.id,
        product_name: product.name,
        brand: product.brand,
        selling_price_incl_vat: product.selling_price_incl_vat,
      });
    } catch (err) {
      console.error('Error exporting product label:', err);
      setErrorModal({ show: true, message: err.message || 'Fout bij exporteren van sticker' });
    } finally {
      setExportingLabel(false);
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <Link to="/products" className="text-blue-600 hover:text-blue-800 mb-4 inline-block">
          ← Back to overview
        </Link>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h1 className="text-3xl font-bold text-gray-900">{product.name}</h1>
            {hasNotes && (
              <div className="relative group">
                <svg
                  className="w-6 h-6 text-blue-600 cursor-help"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <div className="absolute left-0 top-8 hidden group-hover:block z-10 w-64 p-3 bg-gray-800 text-white text-sm rounded-lg shadow-lg">
                  {product.description}
                  <div className="absolute -top-2 left-4 w-0 h-0 border-l-8 border-r-8 border-b-8 border-transparent border-b-gray-800"></div>
                </div>
              </div>
            )}
          </div>
          <button
            onClick={handleExportLabel}
            disabled={exportingLabel}
            className="px-3 py-1.5 text-sm text-blue-600 hover:text-blue-800 border border-blue-600 rounded hover:bg-blue-50 flex items-center gap-1 disabled:opacity-50"
            title="Productsticker exporteren"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
            </svg>
            {exportingLabel ? 'Bezig...' : 'Sticker'}
          </button>
        </div>
        <p className="text-xl text-gray-600 mt-2">{product.brand}</p>
      </div>

      {/* Info Cards - Always visible */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        {/* Stock Card */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Voorraad</h2>
          <div className="space-y-4">
            {/* Row 1: Geschatte en Bevestigde voorraad */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600">Geschatte voorraad</p>
                <p className="text-2xl font-bold text-gray-900">{formatNumber(formattedStockInfo.estimatedStock, 0)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Bevestigde voorraad</p>
                <p className="text-2xl font-bold text-gray-900">
                  {formatNumber(formattedStockInfo.confirmedStock, 0)}
                </p>
              </div>
            </div>
            {/* Row 2: Datum en Telling naam */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600">Datum laatste telling</p>
                <p className="text-lg text-gray-900">
                  {formattedStockInfo.lastCountDate ? formatDate(formattedStockInfo.lastCountDate) : '-'}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Telling</p>
                <p className="text-lg text-gray-900">
                  {formattedStockInfo.lastCountName || '-'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Price Card */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Prijs</h2>
          <div className="space-y-4">
            {/* Row 1: Aankoopprijs excl. korting and incl. korting */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600">Aankoopprijs excl. korting</p>
                <p className="text-2xl font-bold text-gray-900">
                  {prices[0]?.price_excl_discount ? `€${formatNumber(prices[0].price_excl_discount)}` : '-'}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Aankoopprijs incl. korting</p>
                <p className="text-2xl font-bold text-gray-900">
                  {prices[0]?.price_incl_discount ? `€${formatNumber(prices[0].price_incl_discount)}` : '-'}
                </p>
              </div>
            </div>
            {/* Row 2: Verkoopprijs excl. and incl. BTW */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600">Verkoopprijs excl. BTW</p>
                <p className="text-lg text-gray-900">
                  {product.selling_price_excl_vat ? `€${formatNumber(product.selling_price_excl_vat)}` : '-'}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Verkoopprijs incl. BTW</p>
                <p className="text-lg text-gray-900">
                  {product.selling_price_incl_vat ? `€${formatNumber(product.selling_price_incl_vat)}` : '-'}
                </p>
              </div>
            </div>
            {/* Row 3: Datum en Leverancier */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600">Datum laatste aankoop</p>
                <p className="text-lg text-gray-900">
                  {latestInvoicePrice ? formatDate(latestInvoicePrice.recorded_at) : '-'}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Leverancier laatste aankoop</p>
                <p className="text-lg text-gray-900">
                  {latestInvoicePrice?.supplier_name || '-'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Locations */}
      {locations.length > 0 && (
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Locaties</h2>
          <div className="flex flex-wrap gap-2">
            {locations.map((loc) => (
              <Link
                key={loc.id}
                to={`/locations?selected=${loc.id}`}
                className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800 hover:bg-blue-200 transition-colors"
              >
                {loc.description || (loc.code ? `${loc.code} — ${loc.name}` : loc.name)}
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Product Timeline */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Product Historiek</h2>

        {/* Filter Checkboxes */}
        <div className="mb-6 flex flex-wrap gap-4">
          <label className="flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={timelineFilters.purchase}
              onChange={() => toggleFilter('purchase')}
              className="sr-only"
            />
            <div className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${
              timelineFilters.purchase
                ? 'bg-blue-100 text-blue-800'
                : 'bg-gray-100 text-gray-400'
            }`}>
              <div className={`w-4 h-4 rounded flex items-center justify-center ${
                timelineFilters.purchase ? 'bg-blue-600' : 'bg-gray-300'
              }`}>
                {timelineFilters.purchase && (
                  <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </div>
              <span className="font-medium text-sm">Aankoop</span>
            </div>
          </label>

          <label className="flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={timelineFilters.sale}
              onChange={() => toggleFilter('sale')}
              className="sr-only"
            />
            <div className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${
              timelineFilters.sale
                ? 'bg-green-100 text-green-800'
                : 'bg-gray-100 text-gray-400'
            }`}>
              <div className={`w-4 h-4 rounded flex items-center justify-center ${
                timelineFilters.sale ? 'bg-green-600' : 'bg-gray-300'
              }`}>
                {timelineFilters.sale && (
                  <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </div>
              <span className="font-medium text-sm">Verkoop</span>
            </div>
          </label>

          <label className="flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={timelineFilters.price_update}
              onChange={() => toggleFilter('price_update')}
              className="sr-only"
            />
            <div className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${
              timelineFilters.price_update
                ? 'bg-purple-100 text-purple-800'
                : 'bg-gray-100 text-gray-400'
            }`}>
              <div className={`w-4 h-4 rounded flex items-center justify-center ${
                timelineFilters.price_update ? 'bg-purple-600' : 'bg-gray-300'
              }`}>
                {timelineFilters.price_update && (
                  <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </div>
              <span className="font-medium text-sm">Prijswijziging</span>
            </div>
          </label>

          <label className="flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={timelineFilters.stock_count}
              onChange={() => toggleFilter('stock_count')}
              className="sr-only"
            />
            <div className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${
              timelineFilters.stock_count
                ? 'bg-orange-100 text-orange-800'
                : 'bg-gray-100 text-gray-400'
            }`}>
              <div className={`w-4 h-4 rounded flex items-center justify-center ${
                timelineFilters.stock_count ? 'bg-orange-600' : 'bg-gray-300'
              }`}>
                {timelineFilters.stock_count && (
                  <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </div>
              <span className="font-medium text-sm">Voorraadtelling</span>
            </div>
          </label>
        </div>

        <ProductTimeline timeline={filteredTimeline} />
      </div>

      {/* Add Price Modal */}
      {showPriceModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">Nieuwe Prijs Toevoegen</h2>
            <form onSubmit={handleSubmitPrice}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Datum
                </label>
                <input
                  type="date"
                  value={priceFormData.recorded_at}
                  onChange={(e) => setPriceFormData({ ...priceFormData, recorded_at: e.target.value })}
                  className="w-full px-3 py-2 border rounded"
                  required
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Leverancier
                </label>
                <select
                  value={priceFormData.supplier_id}
                  onChange={(e) => setPriceFormData({ ...priceFormData, supplier_id: e.target.value })}
                  className="w-full px-3 py-2 border rounded"
                >
                  <option value="">-- Selecteer leverancier --</option>
                  {suppliers.map((supplier) => (
                    <option key={supplier.id} value={supplier.id}>
                      {supplier.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Prijs excl. korting *
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={priceFormData.price_excl_discount}
                  onChange={(e) => setPriceFormData({ ...priceFormData, price_excl_discount: e.target.value })}
                  className="w-full px-3 py-2 border rounded"
                  placeholder="0.00"
                  required
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Prijs incl. korting *
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={priceFormData.price_incl_discount}
                  onChange={(e) => setPriceFormData({ ...priceFormData, price_incl_discount: e.target.value })}
                  className="w-full px-3 py-2 border rounded"
                  placeholder="0.00"
                  required
                />
              </div>
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Opmerkingen
                </label>
                <textarea
                  value={priceFormData.notes}
                  onChange={(e) => setPriceFormData({ ...priceFormData, notes: e.target.value })}
                  className="w-full px-3 py-2 border rounded"
                  rows="3"
                  placeholder="Optionele opmerkingen..."
                />
              </div>
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowPriceModal(false)}
                  className="px-4 py-2 border rounded hover:bg-gray-100"
                  disabled={loading}
                >
                  Annuleren
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-400"
                  disabled={loading}
                >
                  {loading ? "Bezig..." : "Opslaan"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Error Modal */}
      <ErrorModal
        show={errorModal.show}
        message={errorModal.message}
        onClose={() => setErrorModal({ show: false, message: "" })}
      />
    </div>
  );
}
